import '../../../form/elements/select.css';

import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ReactSelect, { components, MultiValue } from 'react-select';
import { useQuery } from '../../../../hooks/use-query';
import { Option } from '../../../form/elements/select';
import css from './value-filter.module.css';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import ValueFilterActionTypes from '../../../../enums/value-filter-action-types';

const theme = {
  borderRadius: 0,
  colors: {
    primary: '',
    primary75: '',
    primary50: '',
    primary25: '',
    danger: '',
    dangerLight: '',
    neutral0: '',
    neutral5: '',
    neutral10: '',
    neutral20: '',
    neutral30: '',
    neutral40: '',
    neutral50: '',
    neutral60: '',
    neutral70: '',
    neutral80: '',
    neutral90: '',
  },
  spacing: { baseUnit: 0, controlHeight: 0, menuGutter: 0 },
};

export default function ValueFilter({
  name,
  placeholder = '',
  options = [],
  className = 'value-filter',
  filterSearchAction = 'set',
  customClassName,
  customIcon,
  isMulti = false,
  showSelectedCountInsteadOfValue = false,
  closeMenuOnSelect = true,
  isClearable = true,
  isOptionDisabled = undefined,
  onOptionChange = undefined,
  enableClearOnEmpty = false,
  disableAutoSelect = false,
}: {
  name: string;
  options: Option[];
  placeholder?: string;
  className?: string;
  filterSearchAction?: string;
  customClassName?: string;
  customIcon?: IconProp;
  isMulti?: boolean;
  showSelectedCountInsteadOfValue?: boolean;
  closeMenuOnSelect?: boolean;
  isClearable?: boolean;
  isOptionDisabled?: (
    option: Option,
    selectValue?: readonly Option[]
  ) => boolean;
  onOptionChange?: (
    search: URLSearchParams,
    name: string,
    option: Option | MultiValue<Option>,
    isMulti: boolean
  ) => void;
  enableClearOnEmpty?: boolean;
  disableAutoSelect?: boolean;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [params] = useQuery();

  useEffect(() => {
    if (enableClearOnEmpty) {
      if (!params[name]) {
        return;
      }

      const search = new URLSearchParams(history.location.search);

      search.delete(name);
      options
        .filter((option) => {
          if (typeof params[name] === 'string') {
            return option.value === params[name];
          }

          return params[name].includes(option.value);
        })
        .forEach((option) => search.append(name, option.value));

      history.push({ ...history.location, search: search.toString() });
    }
  }, [params, options, enableClearOnEmpty, name, history]);

  const defaultOnOptionChange = useCallback(
    (
      search: URLSearchParams,
      name: string,
      option: Option | MultiValue<Option>,
      isMulti: boolean
    ) => {
      if (option) {
        if (isMulti) {
          search.delete(name);
          const options = option as unknown as MultiValue<Option>;
          options.forEach((option) => search.append(name, option.value));
        } else {
          option = option as unknown as Option;
          switch (filterSearchAction) {
            case ValueFilterActionTypes.set:
              search.set(name, option.value);
              break;
            case ValueFilterActionTypes.append:
              if (!search.getAll(name).includes(option.value)) {
                search.append(name, option.value);
              }
              break;
          }
        }
      } else {
        search.delete(name);
      }
    },
    [filterSearchAction]
  );

  const onChange = useCallback(
    (option: Option | MultiValue<Option>) => {
      const search = new URLSearchParams(history.location.search);

      if (onOptionChange) {
        onOptionChange(search, name, option, isMulti);
      } else {
        defaultOnOptionChange(search, name, option, isMulti);
      }

      search.delete('page');
      history.push({ ...history.location, search: search.toString() });
    },
    [name, history, isMulti, onOptionChange, defaultOnOptionChange]
  );

  const getValueFromParams = () => {
    return options.filter((option) => {
      if (Array.isArray(params[name])) {
        return params[name].includes(option.value);
      }

      return option.value === params[name];
    });
  };

  const CustomDropdownIndicator = (props) => {
    return customIcon ? (
      <components.DropdownIndicator {...props}>
        <FontAwesomeIcon icon={customIcon} />
      </components.DropdownIndicator>
    ) : (
      <components.DropdownIndicator {...props} />
    );
  };

  const CustomValueContainer = useCallback(
    ({
      isDisabled,
      clearValue,
      cx,
      getStyles,
      getClassNames,
      getValue,
      isMulti,
      isRtl,
      options,
      selectOption,
      selectProps,
      setValue,
      theme,
      children,
      ...props
    }) => {
      let childElements = [...children];
      let className = '';
      const optionValues = options.map((item) => item.value);
      const selected = getValue().map((item) => item.value);
      const selectedCount = selected.filter((item) =>
        optionValues.includes(item)
      ).length;

      if (isMulti) {
        className = css['multi-value-container'];
      }

      switch (true) {
        case !!selectProps.inputValue:
          childElements[0] = '';
          break;
        case !disableAutoSelect && options?.length === 1:
          childElements[0] = options[0].label;
          break;
        case selectedCount > 0 && isMulti && showSelectedCountInsteadOfValue:
          childElements[0] = `${selectedCount} ${t(
            'selected_one_or_multiple'
          )}`;
          break;
      }

      return (
        <components.ValueContainer
          {...props}
          isDisabled={isDisabled}
          clearValue={clearValue}
          cx={cx}
          getStyles={getStyles}
          getClassNames={getClassNames}
          getValue={getValue}
          isMulti={isMulti}
          isRtl={isRtl}
          options={options}
          selectOption={selectOption}
          selectProps={selectProps}
          setValue={setValue}
          theme={theme}
          hasValue={true}
          className={className}
        >
          {childElements}
        </components.ValueContainer>
      );
    },
    [t, showSelectedCountInsteadOfValue, disableAutoSelect]
  );

  const CustomOption = (props) => {
    const { data, isSelected } = props;

    return (
      <components.Option {...props}>
        <div className={css['option-container']}>
          <input
            className={css['option-checkbox']}
            type="checkbox"
            checked={isSelected}
            disabled={props.isDisabled}
            readOnly
          />
          {data.label}
        </div>
      </components.Option>
    );
  };

  return (
    <form className={cn(css[className], customClassName)}>
      <ReactSelect
        name={name}
        options={options}
        classNamePrefix="rs"
        placeholder={placeholder}
        loadingMessage={() => t('Loading')}
        noOptionsMessage={() => t('No options')}
        theme={theme}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          ValueContainer: CustomValueContainer,
          Option: CustomOption,
        }}
        value={getValueFromParams()}
        onChange={onChange}
        isClearable={isClearable}
        isMulti={isMulti}
        hideSelectedOptions={false}
        closeMenuOnSelect={closeMenuOnSelect}
        isOptionDisabled={isOptionDisabled}
      />
    </form>
  );
}
